import React, { useEffect, useMemo, useState } from 'react';
import {
  Plus,
  ArrowLeft,
  Save,
  Trash2,
  Layers,
  MapPin,
  Star,
  Info,
  Check,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { confirm, alertDialog } from '../components/ui/confirm-dialog';
import { useToast } from '../components/ui/use-toast';
import { useRestaurant } from '../context/RestaurantContext';
import { useT } from '../context/LanguageContext';
import { restaurantGroupsService } from '../services/restaurantGroupsService';
import { locationsService } from '../services/locationsService';
import { useModuleAccess } from '../utils/useModuleAccess';
import AccessDenied from '../components/AccessDenied';

/* ------------------------------- small inputs ------------------------------ */

const inputCls =
  'w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring/30 focus:border-ring disabled:bg-muted disabled:text-muted-foreground';

function Field({ label, required, children, hint }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium text-muted-foreground">
        {label}{required && <span className="text-destructive"> *</span>}
      </span>
      {children}
      {hint && <span className="mt-1 block text-[11px] text-muted-foreground">{hint}</span>}
    </label>
  );
}

const EMPTY_GROUP = {
  name: '',
  code: '',
  description: '',
  contact_name: '',
  phone: '',
  email: '',
  // Brand colour on the public table finder ('#rrggbb' or blank).
  color: '',
  is_active: true,
};

const fromGroup = (g) => ({
  id: g.id,
  name: g.name || '',
  code: g.code || '',
  description: g.description || '',
  contact_name: g.contact_name || '',
  phone: g.phone || '',
  email: g.email || '',
  color: g.color || '',
  is_active: g.is_active !== false,
  locations: g.locations || [],
});

/* --------------------------------- page ---------------------------------- */

export default function RestaurantGroupsManagement() {
  const { t } = useT();
  const { restaurantId } = useRestaurant();
  const { toast } = useToast();
  // Groups are the branch structure, so they share the Locations permissions.
  const { canView, canEdit, canCreate } = useModuleAccess('locations');

  const [groups, setGroups] = useState([]);
  const [ungrouped, setUngrouped] = useState([]);
  const [allLocations, setAllLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showInactive, setShowInactive] = useState(false);

  // null = list; object = editor for that group (id null = new)
  const [editing, setEditing] = useState(null);
  const [activeTab, setActiveTab] = useState('details');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const load = async () => {
    const [{ groups: gs, ungrouped: ug }, locs] = await Promise.all([
      restaurantGroupsService.list(restaurantId, { includeInactive: showInactive }),
      locationsService.list(restaurantId),
    ]);
    setGroups(gs);
    setUngrouped(ug);
    setAllLocations(locs);
    return gs;
  };

  useEffect(() => {
    if (!restaurantId) return;
    let cancelled = false;
    setLoading(true);
    Promise.all([
      restaurantGroupsService.list(restaurantId, { includeInactive: showInactive }),
      locationsService.list(restaurantId),
    ])
      .then(([{ groups: gs, ungrouped: ug }, locs]) => {
        if (cancelled) return;
        setGroups(gs);
        setUngrouped(ug);
        setAllLocations(locs);
        setError(null);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e?.response?.data?.error || e.message || t('settings.groups.loadFailed'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurantId, showInactive]);

  const openNew = () => {
    setEditing({ id: null, ...EMPTY_GROUP, locations: [] });
    setActiveTab('details');
    setSaveError(null);
  };

  const openExisting = (g) => {
    setEditing(fromGroup(g));
    setActiveTab('details');
    setSaveError(null);
  };

  const closeEditor = () => {
    setEditing(null);
    setSaveError(null);
  };

  const setField = (field, value) => setEditing((prev) => ({ ...prev, [field]: value }));

  const save = async () => {
    if (!restaurantId || !editing) return;
    if (!editing.name?.trim()) {
      setSaveError(t('settings.groups.validation.name'));
      setActiveTab('details');
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const payload = {
        name: editing.name,
        code: editing.code,
        description: editing.description,
        contact_name: editing.contact_name,
        phone: editing.phone,
        email: editing.email,
        color: editing.color,
        is_active: editing.is_active,
      };
      if (editing.id) {
        await restaurantGroupsService.update(restaurantId, editing.id, payload);
        await load();
        setEditing(null);
      } else {
        // A new group normally has no branches yet: they are created under
        // Settings -> Locations, pick this group there, and then show on its
        // Locations tab. So save, say where they go, and return to the list.
        const saved = await restaurantGroupsService.create(restaurantId, payload);
        await load();
        setEditing(null);
        toast({ title: t('settings.groups.created', { name: saved?.name || payload.name }) });
      }
    } catch (e) {
      setSaveError(e?.response?.data?.error || e.message || t('settings.groups.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const removeGroup = async (g) => {
    if (!restaurantId) return;
    if (!(await confirm({
      description: t('settings.groups.confirmDelete', { name: g.name }),
      confirmText: t('settings.groups.confirmDeleteBtn'),
      tone: 'destructive',
    }))) return;
    try {
      await restaurantGroupsService.remove(restaurantId, g.id);
      await load();
    } catch (e) {
      await alertDialog({
        title: t('settings.error'),
        description: `${e?.response?.data?.error || t('settings.groups.deleteFailed')}`,
        tone: 'destructive',
      });
    }
  };

  const onMappingSaved = async (updatedGroup) => {
    const gs = await load();
    const fresh = gs.find((g) => g.id === updatedGroup.id) || updatedGroup;
    setEditing((prev) => (prev && prev.id === fresh.id ? { ...prev, locations: fresh.locations || [] } : prev));
  };

  if (!restaurantId) {
    return (
      <div className="p-6">
        <div className="rounded-md border border-warning/30 bg-warning-soft px-4 py-3 text-sm text-warning">
          {t('settings.groups.noRestaurant')}
        </div>
      </div>
    );
  }

  if (!canView) {
    return <AccessDenied perm="locations.view" what={t('settings.groups.title')} />;
  }

  return (
    <div className="p-6 w-full max-w-full">
      <header className="mb-6 flex items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">{t('settings.groups.eyebrow')}</div>
          <h1 className="font-display text-3xl font-semibold text-foreground tracking-tight leading-none">{t('settings.groups.title')}</h1>
        </div>
        {!editing && (
          <div className="flex items-center gap-4 shrink-0">
            <label className="inline-flex items-center gap-2 text-sm text-muted-foreground cursor-pointer select-none">
              <input
                type="checkbox"
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
                className="h-4 w-4 rounded border-border text-primary focus:ring-ring"
              />
              {t('settings.groups.showInactive')}
            </label>
            {canCreate && (
              <button
                type="button"
                onClick={openNew}
                className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
              >
                <Plus size={16} /> {t('settings.groups.add')}
              </button>
            )}
          </div>
        )}
      </header>

      {!editing && (
        <p className="mb-6 max-w-3xl text-sm text-muted-foreground">{t('settings.groups.intro')}</p>
      )}

      {!canEdit && (
        <div className="mb-4 rounded-md border border-warning/30 bg-warning-soft px-4 py-2.5 text-sm text-warning">
          <strong>{t('settings.readOnly')}</strong> {t('settings.groups.readOnlyBody')}
        </div>
      )}

      {error && (
        <div className="mb-4 rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {error}
        </div>
      )}

      {loading && !editing ? (
        <div className="rounded-md border border-border bg-card px-4 py-8 text-center text-sm text-muted-foreground">
          {t('settings.groups.loading')}
        </div>
      ) : editing ? (
        <GroupEditor
          editing={editing}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          setField={setField}
          onClose={closeEditor}
          onSave={save}
          saving={saving}
          saveError={saveError}
          canEdit={canEdit}
          restaurantId={restaurantId}
          allLocations={allLocations}
          onMappingSaved={onMappingSaved}
        />
      ) : (
        <GroupList
          groups={groups}
          ungrouped={ungrouped}
          onOpen={openExisting}
          onRemove={removeGroup}
          onAdd={openNew}
          canEdit={canEdit}
          canCreate={canCreate}
        />
      )}
    </div>
  );
}

/* --------------------------------- list --------------------------------- */

function GroupList({ groups, ungrouped, onOpen, onRemove, onAdd, canEdit, canCreate }) {
  const { t } = useT();
  const activeUngrouped = ungrouped.filter((l) => l.is_active !== false);

  if (!groups.length) {
    return (
      <div className="rounded-lg border-2 border-dashed border-border bg-card px-6 py-12 text-center">
        <Layers className="mx-auto h-10 w-10 text-muted-foreground" />
        <h3 className="mt-3 text-sm font-semibold text-foreground">{t('settings.groups.empty.title')}</h3>
        <p className="mt-1 text-sm text-muted-foreground">{t('settings.groups.empty.body')}</p>
        {canCreate && (
          <button
            type="button"
            onClick={onAdd}
            className="mt-4 inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            <Plus size={16} /> {t('settings.groups.add')}
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {activeUngrouped.length > 0 && (
        <div className="flex items-start gap-2 rounded-md border border-warning/30 bg-warning-soft px-4 py-2.5 text-sm text-warning">
          <Info size={16} className="mt-0.5 shrink-0" />
          <div>
            <span className="font-medium">
              {t('settings.groups.ungroupedSummary', { count: activeUngrouped.length })}
            </span>
            {' · '}
            {activeUngrouped.map((l) => l.name).join(', ')}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {groups.map((g) => {
          const activeLocs = (g.locations || []).filter((l) => l.is_active !== false);
          return (
            <div
              key={g.id}
              className={`rounded-lg border p-4 transition-all ${
                g.is_active
                  ? 'border-border bg-card hover:border-primary/30 hover:shadow-sm'
                  : 'border-dashed border-border bg-muted'
              }`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className={`text-base font-semibold truncate ${g.is_active ? 'text-foreground' : 'text-muted-foreground'}`}>
                      {g.name}
                    </h3>
                    {g.code && (
                      <span className="inline-flex items-center rounded bg-accent px-1.5 py-0.5 text-[10px] font-mono font-medium text-accent-foreground">
                        {g.code}
                      </span>
                    )}
                    {!g.is_active && (
                      <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
                        {t('settings.groups.inactive')}
                      </span>
                    )}
                  </div>
                  <div className="mt-1 text-xs text-muted-foreground">
                    {t('settings.groups.locationsCount', { count: activeLocs.length })}
                    {g.contact_name ? ` · ${g.contact_name}` : ''}
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    type="button"
                    onClick={() => onOpen(g)}
                    className="text-xs font-medium text-primary hover:underline px-2 py-1"
                  >
                    {canEdit ? t('settings.groups.edit') : t('settings.view')}
                  </button>
                  {canEdit && (
                    <button
                      type="button"
                      onClick={() => onRemove(g)}
                      className="text-muted-foreground hover:text-destructive p-1"
                      title={t('settings.groups.delete')}
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              </div>

              {g.description && (
                <p className="mt-2 text-xs text-muted-foreground line-clamp-2">{g.description}</p>
              )}

              <div className="mt-3 flex flex-wrap gap-1.5">
                {activeLocs.map((l) => (
                  <span
                    key={l.id}
                    className="inline-flex items-center gap-1 rounded-full border border-border bg-background px-2 py-0.5 text-[11px] text-foreground"
                  >
                    <MapPin size={10} className="text-muted-foreground" />
                    {l.name}
                    {l.is_primary && <Star size={9} className="text-warning" />}
                  </span>
                ))}
                {!activeLocs.length && (
                  <span className="text-[11px] italic text-muted-foreground">{t('settings.groups.map.none')}</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* --------------------------------- editor --------------------------------- */

function GroupEditor({
  editing, activeTab, setActiveTab, setField, onClose, onSave, saving, saveError,
  canEdit, restaurantId, allLocations, onMappingSaved,
}) {
  const { t } = useT();
  const TABS = [
    { key: 'details', label: t('settings.groups.tab.details'), icon: Layers },
    { key: 'locations', label: t('settings.groups.tab.locations'), icon: MapPin },
  ];
  const title = editing.id
    ? t(canEdit ? 'settings.groups.editorTitle.edit' : 'settings.groups.editorTitle.view',
        { name: editing.name || t('settings.groups.fallbackName') })
    : t('settings.groups.newGroup');

  return (
    <fieldset disabled={!canEdit} className="border-0 p-0 m-0">
      <div className="rounded-lg border border-border bg-card">
        <div className="border-b border-border px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <button type="button" onClick={onClose} className="text-muted-foreground hover:text-foreground p-1 -ml-1" title={t('settings.back')}>
              <ArrowLeft size={18} />
            </button>
            <h2 className="text-base font-semibold text-foreground truncate">{title}</h2>
          </div>
          <div className="flex items-center gap-2">
            <button type="button" onClick={onClose} className="px-3 py-1.5 text-sm text-foreground hover:bg-muted rounded-md">
              {canEdit ? t('settings.groups.cancel') : t('settings.close')}
            </button>
            {canEdit && activeTab === 'details' && (
              <button
                type="button"
                onClick={onSave}
                disabled={saving}
                className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
              >
                <Save size={14} />
                {saving ? t('settings.groups.saving') : t('settings.groups.save')}
              </button>
            )}
          </div>
        </div>

        <div className="border-b border-border px-5 flex gap-1 overflow-x-auto">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveTab(tab.key)}
                className={`inline-flex items-center gap-2 px-3 py-2 text-sm border-b-2 transition-colors ${
                  isActive ? 'border-primary text-primary font-medium' : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon size={14} /> {tab.label}
              </button>
            );
          })}
        </div>

        <div className="p-5">
          {saveError && activeTab === 'details' && (
            <div className="mb-4 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {saveError}
            </div>
          )}
          {activeTab === 'details' && <DetailsTab editing={editing} setField={setField} />}
          {activeTab === 'locations' && (
            editing.id ? (
              <MappingTab
                restaurantId={restaurantId}
                group={editing}
                allLocations={allLocations}
                canEdit={canEdit}
                onSaved={onMappingSaved}
              />
            ) : (
              <div className="rounded-md border border-dashed border-border bg-muted px-4 py-8 text-center text-sm text-muted-foreground">
                {t('settings.groups.map.saveFirst')}
              </div>
            )
          )}
        </div>
      </div>
    </fieldset>
  );
}

/* ------------------------------ Details tab ------------------------------ */

function DetailsTab({ editing, setField }) {
  const { t } = useT();
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl">
      <Field label={t('settings.groups.field.name')} required>
        <input
          className={inputCls}
          value={editing.name}
          onChange={(e) => setField('name', e.target.value)}
          placeholder={t('settings.groups.field.namePh')}
        />
      </Field>
      <Field label={t('settings.groups.field.code')} hint={t('settings.groups.field.codeHint')}>
        <input
          className={`${inputCls} font-mono uppercase`}
          value={editing.code}
          onChange={(e) => setField('code', e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 10))}
          placeholder="NORTH"
          maxLength={10}
        />
      </Field>
      <Field label={t('settings.groups.field.contact')}>
        <input
          className={inputCls}
          value={editing.contact_name}
          onChange={(e) => setField('contact_name', e.target.value)}
        />
      </Field>
      <Field label={t('settings.groups.field.phone')}>
        <input
          className={inputCls}
          value={editing.phone}
          onChange={(e) => setField('phone', e.target.value)}
          placeholder="5 123 4567"
        />
      </Field>
      <Field label={t('settings.groups.field.email')}>
        <input
          type="email"
          className={inputCls}
          value={editing.email}
          onChange={(e) => setField('email', e.target.value)}
          placeholder="group@example.com"
        />
      </Field>
      <Field label={t('settings.groups.field.color')} hint={t('settings.groups.field.colorHint')}>
        <div className="flex items-center gap-2">
          <input
            type="color"
            aria-label={t('settings.groups.field.color')}
            value={/^#[0-9a-fA-F]{6}$/.test(editing.color) ? editing.color : '#dcc79a'}
            onChange={(e) => setField('color', e.target.value.toLowerCase())}
            className="h-10 w-12 rounded border border-input bg-background p-1"
          />
          <input
            className={`${inputCls} font-mono`}
            value={editing.color}
            onChange={(e) => setField('color', e.target.value.trim().toLowerCase().slice(0, 7))}
            placeholder="#1f7a4c"
            maxLength={7}
          />
        </div>
      </Field>
      <div className="flex items-end">
        <label className="inline-flex items-center gap-2 text-sm text-foreground cursor-pointer select-none">
          <input
            type="checkbox"
            checked={!!editing.is_active}
            onChange={(e) => setField('is_active', e.target.checked)}
            className="h-4 w-4 rounded border-border text-primary focus:ring-ring"
          />
          {t('settings.groups.field.active')}
        </label>
      </div>
      <div className="md:col-span-2">
        <Field label={t('settings.groups.field.description')}>
          <textarea
            rows={3}
            className={inputCls}
            value={editing.description}
            onChange={(e) => setField('description', e.target.value)}
            placeholder={t('settings.groups.field.descriptionPh')}
          />
        </Field>
      </div>
    </div>
  );
}

/* ------------------------------ Mapping tab ------------------------------ */

function MappingTab({ restaurantId, group, allLocations, canEdit, onSaved }) {
  const { t } = useT();
  const initial = useMemo(() => new Set((group.locations || []).map((l) => l.id)), [group.locations]);
  const [selected, setSelected] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [savedAt, setSavedAt] = useState(null);

  useEffect(() => { setSelected(initial); }, [initial]);

  // The group's own branches, as the group payload reports them (fresh again
  // after a mapping save). Branches are created under Settings -> Locations
  // with this group picked, so a new group starts empty and fills up later.
  const own = (group.locations || []).filter((l) => l.is_active !== false);
  // Branches sitting in another group, which can be moved in from here.
  const others = allLocations.filter((l) => l.is_active !== false && !initial.has(l.id));

  const dirty = useMemo(() => {
    for (const id of selected) if (!initial.has(id)) return true;
    return false;
  }, [selected, initial]);

  const toggle = (id) => {
    setSavedAt(null);
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const { group: updated } = await restaurantGroupsService.setLocations(restaurantId, group.id, [...selected]);
      setSavedAt(Date.now());
      await onSaved(updated);
    } catch (e) {
      setError(e?.response?.data?.error || e.message || t('settings.groups.saveFailed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-3xl space-y-6">
      <section>
        <h3 className="text-sm font-semibold text-foreground">{t('settings.groups.map.title')}</h3>
        {own.length === 0 ? (
          <div className="mt-2 rounded-md border border-dashed border-border bg-muted px-4 py-6 text-center text-sm text-muted-foreground">
            <p>{t('settings.groups.map.own.empty')}</p>
            <Link
              to="/settings/locations"
              className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
            >
              <MapPin size={14} /> {t('settings.groups.map.own.goto')}
            </Link>
          </div>
        ) : (
          <ul className="mt-2 divide-y divide-border rounded-md border border-border bg-background">
            {own.map((l) => (
              <li key={l.id} className="flex items-center gap-3 px-3 py-2.5">
                <MapPin size={14} className="shrink-0 text-muted-foreground" />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-foreground truncate">{l.name}</span>
                    {l.is_primary && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-warning-soft px-2 py-0.5 text-[10px] font-medium text-warning">
                        <Star size={10} /> {t('settings.groups.map.primary')}
                      </span>
                    )}
                  </div>
                  {l.city && <div className="text-[11px] text-muted-foreground">{l.city}</div>}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {others.length > 0 && (
        <section>
          <h3 className="text-sm font-semibold text-foreground">{t('settings.groups.map.move.title')}</h3>
          <p className="mt-1 text-xs text-muted-foreground">{t('settings.groups.map.move.hint')}</p>
          {canEdit && (
            <div className="mt-2 flex items-center gap-3 text-xs">
              <button
                type="button"
                className="text-primary hover:underline"
                onClick={() => { setSavedAt(null); setSelected(new Set([...initial, ...others.map((l) => l.id)])); }}
              >
                {t('settings.groups.map.selectAll')}
              </button>
            </div>
          )}
          <ul className="mt-2 divide-y divide-border rounded-md border border-border bg-background">
            {others.map((l) => {
              const checked = selected.has(l.id);
              const currentGroupName = l.group && l.group.id !== group.id ? l.group.name : null;
              return (
                <li key={l.id} className="flex items-center gap-3 px-3 py-2.5">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggle(l.id)}
                    className="h-4 w-4 rounded border-border text-primary focus:ring-ring"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-foreground truncate">{l.name}</span>
                      {l.is_primary && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-warning-soft px-2 py-0.5 text-[10px] font-medium text-warning">
                          <Star size={10} /> {t('settings.groups.map.primary')}
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] text-muted-foreground">
                      {[l.city,
                        currentGroupName
                          ? t('settings.groups.map.inGroup', { group: currentGroupName })
                          : (!l.group ? t('settings.groups.map.ungrouped') : null),
                      ].filter(Boolean).join(' · ')}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
          {error && (
            <div className="mt-3 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</div>
          )}
          {canEdit && (
            <div className="mt-3 flex items-center gap-3">
              <button
                type="button"
                onClick={save}
                disabled={busy || !dirty}
                className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
              >
                <Save size={14} />
                {busy ? t('settings.groups.saving') : t('settings.groups.map.save')}
              </button>
              {savedAt && !dirty && (
                <span className="inline-flex items-center gap-1 text-xs text-success">
                  <Check size={12} /> {t('settings.groups.map.saved')}
                </span>
              )}
            </div>
          )}
        </section>
      )}
    </div>
  );
}